import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/utils/api';

interface Member {
    userId: string;
    name: string;
    email: string;
    role: 'owner' | 'admin' | 'member';
}

export function OrgAdmin() {
    const { orgId } = useParams<{ orgId: string }>();
    const [members, setMembers] = useState<Member[]>([]);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMembers();
    }, [orgId]);

    const loadMembers = async () => {
        if (!orgId) return;

        try {
            const data = await api.getOrgMembers(orgId);
            setMembers(data);
        } catch (error) {
            console.error('Failed to load members:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async () => {
        if (!orgId) return;

        try {
            // In a real implementation, this would look up user by email
            // For now, we'll just show a placeholder
            alert('Invite functionality requires backend implementation');
            setIsInviteModalOpen(false);
            setInviteEmail('');
        } catch (error) {
            console.error('Failed to invite member:', error);
        }
    };

    const handleUpdateRole = async (userId: string, newRole: string) => {
        if (!orgId) return;

        try {
            await api.updateOrgMember(orgId, userId, { role: newRole });
            await loadMembers();
            alert('Role updated successfully');
        } catch (error) {
            console.error('Failed to update role:', error);
            alert('Failed to update role');
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!orgId) return;
        if (!confirm('Are you sure you want to remove this member?')) return;

        try {
            await api.removeOrgMember(orgId, userId);
            await loadMembers();
            alert('Member removed');
        } catch (error) {
            console.error('Failed to remove member:', error);
            alert('Failed to remove member');
        }
    };

    const roleOptions = [
        { value: 'owner', label: 'Owner' },
        { value: 'admin', label: 'Admin' },
        { value: 'member', label: 'Member' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            <main style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-2xl)' }}>
                <div className="container" style={{ maxWidth: '900px' }}>
                    <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <div>
                            <h1 style={{ marginBottom: 'var(--spacing-sm)' }}>Member Management</h1>
                            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 0 }}>
                                Manage organization members and their roles
                            </p>
                        </div>
                        <Button variant="primary" onClick={() => setIsInviteModalOpen(true)}>
                            + Invite Member
                        </Button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 'var(--spacing-2xl)' }}>
                            Loading...
                        </div>
                    ) : (
                        <div className="panel">
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                        <th style={{ textAlign: 'left', padding: 'var(--spacing-md)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                                            Member
                                        </th>
                                        <th style={{ textAlign: 'left', padding: 'var(--spacing-md)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                                            Role
                                        </th>
                                        <th style={{ textAlign: 'right', padding: 'var(--spacing-md)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map(member => (
                                        <tr key={member.userId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                            <td style={{ padding: 'var(--spacing-md)' }}>
                                                <div style={{ fontWeight: 500 }}>{member.name}</div>
                                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                                    {member.email}
                                                </div>
                                            </td>
                                            <td style={{ padding: 'var(--spacing-md)' }}>
                                                <div style={{ maxWidth: '150px' }}>
                                                    <Select
                                                        value={member.role}
                                                        onValueChange={(value) => handleUpdateRole(member.userId, value)}
                                                        options={roleOptions}
                                                    />
                                                </div>
                                            </td>
                                            <td style={{ padding: 'var(--spacing-md)', textAlign: 'right' }}>
                                                {member.role !== 'owner' && (
                                                    <Button
                                                        variant="ghost"
                                                        onClick={() => handleRemoveMember(member.userId)}
                                                        style={{ color: 'var(--color-error)' }}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>

            {/* Invite Modal */}
            <Modal
                open={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                title="Invite Member"
            >
                <div className="flex flex-col gap-lg">
                    <Input
                        label="Email Address"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="user@example.com"
                    />
                    <Select
                        value={inviteRole}
                        onValueChange={(value) => setInviteRole(value as 'admin' | 'member')}
                        options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'member', label: 'Member' },
                        ]}
                        label="Role"
                    />
                    <div className="flex gap-md" style={{ justifyContent: 'flex-end' }}>
                        <Button variant="ghost" onClick={() => setIsInviteModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleInvite} disabled={!inviteEmail}>
                            Send Invite
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
